import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ReactNode } from "react";

export const PURCHASE_TERMS_VERSION = "v1-2026-04";

interface PurchaseTermsDialogProps {
  trigger: ReactNode;
}

/**
 * Full-text Termos de Compra, Acesso e Reembolso modal.
 * Content language is PT-BR per the legal copy provided by the platform.
 */
export function PurchaseTermsDialog({ trigger }: PurchaseTermsDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="text-lg">
            Termos de Compra, Acesso e Reembolso
          </DialogTitle>
          <DialogDescription className="text-xs">
            Versão {PURCHASE_TERMS_VERSION} — leia com atenção antes de finalizar a compra.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh]">
          <div className="px-6 py-5 space-y-5 text-sm leading-relaxed text-foreground/90">
            <p>
              Ao finalizar esta compra, declaro que li e concordo com as condições abaixo:
            </p>

            <Section title="1. Funcionamento da plataforma">
              A Aicavite é uma plataforma digital de cursos online. Após a confirmação do pagamento,
              o acesso ao curso comprado será liberado na conta do aluno, dentro da área "Meus Cursos"
              ou área equivalente da plataforma.
            </Section>

            <Section title="2. Liberação de acesso">
              O acesso ao curso só será liberado após a confirmação do pagamento pelo meio escolhido,
              como Pix, cartão ou outro método disponível no checkout. Pagamentos pendentes, recusados,
              cancelados ou não confirmados não liberam acesso ao curso.
            </Section>

            <Section title="3. Prazo de acesso ao curso">
              Alguns cursos possuem acesso vitalício e outros possuem acesso por prazo determinado.
              Quando o curso tiver duração limitada, o prazo de acesso será informado antes da compra,
              como 1 mês, 2 meses, 3 meses ou outro período indicado na oferta.
            </Section>

            <Section title="4. Contagem do prazo">
              Para cursos com prazo determinado, o período de acesso começa a contar a partir da
              confirmação do pagamento. Exemplo: se um curso de 2 meses for pago e aprovado em 10 de
              março, o acesso ficará disponível até 10 de maio, salvo regra diferente expressamente
              informada na oferta.
            </Section>

            <Section title="5. Renovação de acesso">
              Quando disponível, o aluno poderá renovar o acesso ao curso realizando um novo pagamento.
              A renovação adicionará um novo período de acesso conforme a duração configurada para o curso.
              Exemplo: se o curso tem duração de 2 meses, a renovação concederá mais 2 meses de acesso.
            </Section>

            <Section title="6. Avisos de vencimento">
              A plataforma poderá enviar avisos por e-mail, notificação interna ou outro canal cadastrado
              informando que o acesso ao curso está próximo do vencimento. Caso o aluno não renove, o acesso
              ao conteúdo poderá ser bloqueado após o término do prazo informado, incluindo eventual período
              de tolerância de 2 dias indicado pela plataforma.
            </Section>

            <Section title="7. Certificado">
              Se o aluno concluir o curso e cumprir os requisitos de certificação antes do bloqueio do acesso,
              o certificado emitido continuará disponível na área de certificados da conta, mesmo que o acesso
              ao conteúdo do curso expire posteriormente. O curso expirado poderá deixar de aparecer em
              "Meus Cursos ativos", mas poderá aparecer em área de cursos expirados, concluídos ou renováveis.
            </Section>

            <Section title="8. Direito de arrependimento">
              Em compras realizadas pela internet, o consumidor pode exercer o direito de arrependimento no
              prazo legal de <strong>7 dias</strong>, conforme o Código de Defesa do Consumidor. O prazo será
              contado conforme a legislação aplicável à contratação do serviço digital. Para solicitar o
              cancelamento dentro desse prazo, o aluno deverá entrar em contato pelos canais oficiais de
              suporte da plataforma.
            </Section>

            <Section title="9. Reembolso">
              Quando o pedido de cancelamento for feito dentro do prazo legal de arrependimento, o valor pago
              será reembolsado pelo mesmo meio de pagamento utilizado, sempre que tecnicamente possível, ou
              por outro meio acordado com o consumidor. Após o reembolso, o acesso ao curso poderá ser cancelado
              e eventual certificado relacionado à compra reembolsada poderá ser revogado, quando aplicável.
            </Section>

            <Section title="10. Cancelamentos fora do prazo legal">
              Após o prazo legal de arrependimento, pedidos de cancelamento ou reembolso serão analisados conforme
              a política interna da plataforma, o tipo de curso adquirido, o tempo de acesso já utilizado, eventuais
              falhas técnicas comprovadas e a legislação aplicável.
            </Section>

            <Section title="11. Problemas técnicos">
              Caso o aluno tenha dificuldade de acesso causada por falha da plataforma, deverá entrar em contato
              com o suporte para análise e correção. Problemas causados por conexão de internet, dispositivo
              incompatível, navegador desatualizado ou dados incorretos informados pelo aluno poderão depender
              de orientação técnica, sem necessariamente gerar reembolso automático.
            </Section>

            <Section title="12. Dados e responsabilidade">
              O aluno é responsável por manter seus dados de acesso seguros e atualizados. O acesso ao curso é
              pessoal e intransferível, salvo autorização expressa da plataforma.
            </Section>

            <Section title="13. Aceite">
              Ao clicar em "Li e concordo" e finalizar a compra, confirmo que entendi o funcionamento da plataforma,
              o prazo de acesso do curso, as regras de renovação, a política de cancelamento e reembolso, e autorizo
              a liberação do acesso conforme a confirmação do pagamento.
            </Section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="font-semibold text-foreground">{title}</h3>
      <p>{children}</p>
    </section>
  );
}
